// src/auditoria/components/ds/tokens.ts
//
// Tokens do canvas (Kit Canvas Fiscaliza — UI-2, DESIGN-SYSTEM.md §2).
//
// São os valores que o tema do MUI não alcança porque descrevem marcação, não
// estilo: raio de pílula, altura de barra, altura de linha de grade, o par de
// cores do gradiente e a ordem das séries dos gráficos do canvas.
//
// Conferência contra o tema do app (nenhuma divergência encontrada):
//   - `shape.borderRadius` = 8 em `src/theme/create-theme.ts:35`
//     → DS_BOX_RADIUS = 8 e DS_CARD_RADIUS = 16 (o dobro).
//   - paleta de `src/theme/theme-config.ts`: primary #00A76F / dark #007867 /
//     light #5BE49B; secondary #8E33FF / light #C684FF; info #00B8D9 /
//     light #61F3F3; success #22C55E; warning #FFAB00 / light #FFD666;
//     error #FF5630 / light #FFAC82; grey 300 #DFE3E8, 500 #919EAB,
//     700 #454F5B, 800 #1C252E. `background.neutral` = grey[200] #F4F6F8
//     (light) / #28323D (dark), em `src/theme/core/palette.ts:97-99`.
//     Todos batem com os literais de DS_BAR_GRADIENT abaixo.
//
// Observação: `src/theme/theme-overrides.ts` declara um primary roxo (#6950E8),
// mas esse módulo não é importado em lugar nenhum da árvore — o primary efetivo
// continua sendo o #00A76F do `theme-config.ts`. Se um dia esse override entrar
// em uso, DS_BAR_GRADIENT.primary precisa ser revisto.

export const DS_PILL_RADIUS = 999;
export const DS_BOX_RADIUS = 8; // = theme.shape.borderRadius
export const DS_CARD_RADIUS = 16; // = theme.shape.borderRadius * 2
export const DS_BAR_HEIGHT = 8;
export const DS_GANTT_HEIGHT = 14;
export const DS_ROW_HEIGHT = 44;
export const DS_HEAD_HEIGHT = 38;

// ----------------------------------------------------------------------
// Densidade compacta da grade — variante OPT-IN (ver `SX_GRADE_CANVAS_COMPACTA`).
//
// Pedido do dono do produto olhando a tela `/dashboard/atividade-fiscal/
// malha-fiscal/execucao`: "deixa essa tabela mais compacta, porém com as mesmas
// informações". Nenhuma coluna sai e nada vai para trás de tooltip; só a altura
// morta encolhe.
//
// Conferência contra o artboard (`auditoria_design_canvas/malha-execucao.dc.html`
// e `malha-execucao-consultar.dc.html`):
//   - `td{font-size:12px}` e `td{line-height:1.35}` -> o kit estava em 13px sem
//     `lineHeight`. Descer para 12/1.35 é ADERÊNCIA, não capricho.
//   - o artboard não tem altura mínima de linha: o `.trow` é `padding:10px 0` e a
//     linha nasce do conteúdo (~36px numa linha de texto puro). O piso de 44 do
//     kit é mais folgado que o desenho.
//   - DIVERGÊNCIA CONSCIENTE: o `th` do artboard é `padding:11px 8px` sobre 14px,
//     ou seja ~43px de cabeçalho, e o `td` paga 10px em cima e embaixo. Aqui o
//     cabeçalho vai a 32 e a célula a 6. Foi o dono do produto que pediu mais
//     densidade do que o canvas desenha — mesma natureza da divergência já
//     registrada na faixa do `.thead` e no ícone de 20px de `SX_ACAO`.
//
// Onde parou: 12px é o piso de legibilidade do corpo (abaixo disso vira ilegível)
// e 34px de linha ainda abriga o `DsStatusChip`, que mede 21px de altura, com os
// 6+6 de respiro da célula — 21+12 = 33, dentro dos 34. Não dá para descer mais
// sem espremer o chip.
// ----------------------------------------------------------------------

export const DS_ROW_HEIGHT_COMPACTA = 34;
export const DS_HEAD_HEIGHT_COMPACTA = 32;
/** Respiro vertical da célula na variante compacta, em px (artboard: 10). */
export const DS_CELL_PAD_Y_COMPACTA = 6;
/** Corpo da célula compacta — literal do `td` do artboard. Piso de legibilidade. */
export const DS_CELL_FONT_COMPACTA = 12;
/** `line-height` do `td` de `malha-execucao-consultar.dc.html`. */
export const DS_CELL_LINE_COMPACTA = 1.35;
export const DS_TABULAR = { fontVariantNumeric: 'tabular-nums' } as const;
export const DS_MONO = 'ui-monospace, "Roboto Mono", Menlo, monospace';

/** Pares light -> main do gradiente de barra (valores literais de dte-dashboard). */
export const DS_BAR_GRADIENT = {
  primary: ['#5BE49B', '#00A76F'],
  secondary: ['#C684FF', '#8E33FF'],
  warning: ['#FFD666', '#FFAB00'],
  error: ['#FFAC82', '#FF5630'],
  info: ['#61F3F3', '#00B8D9'],
} as const;

/** Ordem de cor das séries de rosca/barra do canvas (≠ colors do use-chart). */
export const DS_SERIES = [
  'warning.main',
  'primary.main',
  'success.main',
  'error.main',
  'grey.500',
  'primary.dark',
] as const;
